import React, { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useLoader } from "../../context/LoaderContext";
import { ColDef } from "ag-grid-enterprise";
import { useNavigate } from "react-router-dom";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import {
  getMetaDataGroups,
  metaDataGroupStatusChange,
} from "../../api/metaDataGroupsApi/metaDataGroupsApi";
import moment from "moment";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import SearchIcon from "@mui/icons-material/Search";
import { Tooltip, IconButton, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import CloseIcon from "@mui/icons-material/Close";
import { getExcelData } from "../../api/groupsApi/groupsApi";
import { useSnackbar } from "../Common/SnackbarContext";
import { appSettings } from "../../config/appSetting";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

interface MetadataGroupsMangementProps {
  businessUnitData: any;
}

const MetadataGroupsMangement: React.FC<MetadataGroupsMangementProps> = ({
  businessUnitData,
}) => {
  const { setLoading } = useLoader();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showEmptySearchMessage, setShowEmptySearchMessage] = useState(false);
  const [, setRecordsFound] = useState(0); // NOSONAR
  const { showSnackbar } = useSnackbar();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const navigation = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const handleConfirmDialogClose = async (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed && pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  // Helper function extracted outside the Actions component
  const performStatusChange = async (
    groupid: number,
    metadataGroupsModel: any,
    getData: (model: any) => Promise<void>,
    setRecordsFound: (count: number) => void
  ) => {
    const payload = { Groupid: groupid };
    try {
      const response = await metaDataGroupStatusChange(payload);
      if (response) {
        await getData(metadataGroupsModel);
      }
      setRecordsFound(response.metadataGroupModel.length);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  // Refactored Actions component
  const Actions = (props: any) => {
    const { data } = props;
    const selectedRowData = props.data;

    const handleEditAction = () => {
      navigation("/newMetadataGroup", {
        state: { rowData: selectedRowData, action: "Edit" },
      });
    };

    const handleStatusChangeClick = () => {
      setPendingAction(
        () => () =>
          performStatusChange(
            selectedRowData.groupid,
            metadataGroupsModel,
            getData,
            setRecordsFound
          )
      );
      setConfirmDialogOpen(true);
    };

    return (
      <>
        {isPagePermission("Allow Edit", "MetaDataGroup", 723) && (
          <Tooltip title="Click to edit the group">
            <EditIcon
              className="icon-button-other"
              onClick={handleEditAction}
              style={{ padding: "0.2vh", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        )}
        {data?.active ? (
          <Tooltip title="Click to deactivate the group">
            <PlayCircleIcon
              className="icon-button-other"
              onClick={handleStatusChangeClick}
              style={{ padding: "0.2vh", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to activate the group">
            <DoDisturbIcon
              className="icon-button-other"
              onClick={handleStatusChangeClick}
              style={{ padding: "0.2vh", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        )}
      </>
    );
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "rowIndex",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    {
      headerName: "Group Name",
      field: "groupName",
      tooltipField: "groupName",
      width: 250,
      colId: "groupName",
      flex: 1,
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Created On",
      field: "createdDate",
      tooltipField: "createdDate",
      width: 250,
      colId: "createdDate",
      flex: 1,
      enableRowGroup: true,
      valueFormatter: (params) => {
        return params.value
          ? moment.utc(params.value).local().format("MM-DD-YYYY HH:mm:ss")
          : "";
      },
      filter: true,
    },
    {
      headerName: "Updated On",
      field: "lastModifiedDate",
      tooltipField: "lastModifiedDate",
      width: 250,
      colId: "lastModifiedDate",
      flex: 1,
      enableRowGroup: true,
      valueFormatter: (params) => {
        return params.value
          ? moment.utc(params.value).local().format("MM-DD-YYYY HH:mm:ss")
          : "";
      },
      filter: true,
    },
    {
      headerName: "Updated By",
      valueGetter: (params) => {
        const { firstName, lastName } = params.data || {};
        return `${firstName || ""} ${lastName || ""}`.trim();
      },
      tooltipValueGetter: (params) => {
        const { firstName, lastName } = params.data || {};
        return `${firstName || ""} ${lastName || ""}`.trim();
      },
      width: 270,
      flex: 1,
      colId: "updatedBy",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Actions",
      field: "link",
      cellRenderer: Actions,
      lockPinned: true,
      suppressSizeToFit: true,
      width: 100,
      minWidth: 100,
      pinned: "right",
    },
  ]);

  const getData = async (updateMetaData: any) => {
    setLoading(true);
    try {
      const response = await getMetaDataGroups(updateMetaData);
      setRowData(response.metadataGroupModel);

      if (updateMetaData && searchTerm) {
        showSnackbar(response.totalcounts + " records found.");
      }
      setRecordsFound(response.metadataGroupModel.length);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(metadataGroupsModel);
  }, []);

  const params: any = {
    AccountId:
      businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
    IsSuperAdmin: 1,
    SearchText: searchTerm,
    aliasname: "MetadataGroups",
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    isexporttpExcel: true,
    offset: appSettings.timezone,
    pagename: "MetadataGroups",
    sortOrder: 0,
  };

  const excelData = async () => {
    try {
      const excelDataResponse = await getExcelData(params);
      const blob = new Blob([excelDataResponse], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const date = new Date();
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date
        .getDate()
        .toString()
        .padStart(2, "0")}-${date.getFullYear()}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `MetadataGroups Reports_${formattedDate}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
      showSnackbar("File downloaded successfully!");
    } catch (error) {
      console.error("Error fetching Excel data", error);
    }
  };

  const handelCreateNew = () => {
    navigation("/newMetadataGroup");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  let metadataGroupsModel: any = {
    IsSuperAdmin: 1,
    SearchText: "",
    pagename: "MetadataGroups",
    currentPage: 1,
    isexporttpExcel: false,
    noOfRecords: 25,
  };
  const handleSearchClick = async () => {
    if (!searchTerm) {
      setShowEmptySearchMessage(true);
      return;
    }
    let updateMetaData: any = {
      ...metadataGroupsModel,
      SearchText: searchTerm,
    };
    await getData(updateMetaData);
    setShowEmptySearchMessage(false);
  };

  const handleCloseSearch = async () => {
    setSearchTerm("");
    setShowEmptySearchMessage(true);
    await getData(metadataGroupsModel);
  };

  //Quick Filter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);

  return (
    <div style={{ marginLeft: "2vh" }}>
      <NavigationBar
        menuTitle="Configuration"
        title="Metadata Management"
        screenTitle="Metadat Groups"
        text="Metadata Groups"
      />
      <div className="searchContainer" style={{ marginTop: "2vh" }}>
        <div className="searchWrapper">
          <div
            style={{
              display: "none",
              alignItems: "center",
              marginTop: "2vh",
              marginLeft: "2vh",
            }}
          >
            <input
              type="text"
              placeholder="Search By Group Name"
              value={searchTerm}
              onChange={handleSearchChange}
              className="searchInput"
              data-testid="search-input"
            />
            <button onClick={handleSearchClick} className="searchButton">
              <SearchIcon style={{ color: "#006ab6" }} />
            </button>
            {searchTerm && (
              <IconButton
                onClick={handleCloseSearch}
                style={{ padding: "0.5vh" }}
              >
                <CloseIcon style={{ color: "var(--aggrid-text-color)" }} />
              </IconButton>
            )}
          </div>

          <input
            type="text"
            id="filter-text-box"
            placeholder="Quick Filter..."
            style={{
              width: "25em",
              height: "2.5em",
              fontSize: "11px",
              paddingLeft: ".5em",
              marginBottom: ".1em",
              marginLeft: "1.5em",
            }}
            onInput={onFilterTextBoxChanged}
          />

          {showEmptySearchMessage && (
            <span className="errorMessage" style={{ marginLeft: "2vh" }}>
              Please enter text for search
            </span>
          )}
        </div>
        <div
          style={{ display: "flex", justifyContent: "end", marginRight: "2em" }}
        >
          {isPagePermission("Allow Create New", "MetaDataGroup", 721) && (
            <Button
              variant="contained"
              size="small"
              className="button create-new-button"
              onClick={handelCreateNew}
            >
              Create New
            </Button>
          )}
          {isPagePermission("Allow Export to Excel", "MetaDataGroup", 722) && (
            <Tooltip title="Click to export the grid content in excel">
              <button
                type="button"
                onClick={excelData}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                aria-label="Export to Excel"
              >
                <img
                  src={ExcelIcon}
                  alt="Excel Icon"
                  className="excel-icon"
                  style={{ marginTop: ".3em", marginRight: "1em" }}
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="rp-main-inner col-sm-12">
        <div className="rp-main-inner col-sm-12">
          <div
            className="ag-theme-quartz-dark"
            style={{
              height: "66vh",
              marginTop: "1vh",
              width: "98%",
              marginLeft: "1.5vh",
            }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              pagination={true}
              paginationPageSize={defaulPageSize}
              paginationPageSizeSelector={pageSizeSelector}
              rowGroupPanelShow="always"
              pivotPanelShow="always"
              ref={gridRef}
              headerHeight={30}
              gridOptions={{
                rowSelection: {
                  mode: "multiRow",
                  checkboxes: true,
                  headerCheckbox: true,
                  enableClickSelection: false,
                },
              }}
              loading={!rowData}
              overlayNoRowsTemplate={
                '<span class="ag-overlay-loading-center">No rows to show</span>'
              }
              loadingOverlayComponent={CustomLoadingOverlay}
            />
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        title="Metadata Group - Update Metadata Group"
        content="Please note that the Metadata-Group is mapped with the Metadata-Field. Are you sure you want to proceed?"
      />
    </div>
  );
};

export default MetadataGroupsMangement;
